﻿import { computed, ComputedRef, Ref, ref, ShallowRef, shallowRef } from 'vue';
import {
    checkIdentityConflict,
    CheckIdentityResult,
    checkUniquenessConflict,
    CheckUniquenessResult,
    CodeEditContextRefCode,
    CodeEditContextRequest,
    fetchCodeEditContext,
    ResponsePromise
} from '@/shared/helpers/api.ts';
import { ChangeDocumentRequestCode, createIdentityChangeDocument } from '@/shared/codeedit/changeDocument.ts';
import { http } from '@/shared/httpWrapper';
import { makeValidator } from '@/shared/helpers/inputRegexValidator.ts';
import {
    ApplyToCodeFunc,
    ApplyToCodeSetFunc,
    ApplyToCodeSetsResult,
    ApplyToCodesResult,
    ApplyToLibrariesResult,
    ApplyToLibraryFunc,
    CodeEditSessionItem,
    EditLibraries,
    ErrorType,
    LibrariesResult,
    RegexValidators,
    ValidationError
} from '@/shared/codeedit/codeEditSessionItem.ts';
import {
    createFieldDefinitionsWithCustomLabels,
    FieldDefinition,
    isHidden,
    isStringField
} from '@/shared/codeedit/field.ts';
import {
    AttributeValue,
    choiceViolation,
    CodeEditFields,
    codeEditFieldsDiff,
    codeEditFieldsFromCodeResponse,
    codeEditFieldsRequiredMissing,
    codeEditRequiredFieldValidValue,
    newCodeEditFields
} from '@/shared/codeedit/codeEditFields.ts';
import {
    CodeEditContextResponse,
    CodeResponse,
    LibraryResponse
} from '@/shared/helpers/api/types/codeEditContextResponse.ts';
import _ from 'lodash';
import {
    brokenDynamicFilterDependency,
    evaluateReferenceFilter,

    referenceFilterMismatchMessage,
    valueChangedForFilter,
    isCaseSensitive
} from '@/shared/codeedit/dynamicFilter.ts';
import { typeGroup } from '@/shared/codeedit/dynamicFilterRules.ts';
import { CaseSensitiveCodeRefs } from '@/shared/codeedit/dynamicFilterTypes.ts';

export const newCodeDefaultId = 3_000_000_000; // id is only used in UI, so here we just pick a number that is higher than what can be used in the backend.

type UniquenessCheckRequest = {
    field: string;
    value: string;
};

export interface IEditSessionState {
    collection: Ref<EditLibraries>;
    numCodeSetsWithChanges: ComputedRef<number>;
    canSubmit: ComputedRef<boolean>;
    canSubmitCauses: ComputedRef;

    applyToAllCodes<T>(f: ApplyToCodeFunc<T>): ApplyToCodesResult<T>[];

    checkIdentities(): Promise<void>;

    validateInputs(): void;

    clearChanges(): void;

    checkUniqueness(): Promise<void>;
}

export class EditSessionState implements IEditSessionState {
    collection: Ref<EditLibraries>;
    /*
    We use a shallow ref to store the referable codes. This is because the object can be really large (fields with 10 000+ array elements), and makes the page unusable when vue dev-tools renders these objects.
     */
    referableCodes: ShallowRef<Record<string, Record<string, CodeEditContextRefCode[]>>>; // codeSet -> field -> codeEditCtxRefCode

    fieldDefs: Ref<Record<string, FieldDefinition[]>>; // libName --> fieldDefinitions
    regexValidators: Ref<Record<string, RegexValidators>>;// codeset -> validators
    lastIdentityCheck: Ref<string | null>;
    lastUniquenessCheck: Ref<string | null>;
    newCodeSequenceNumber: Ref<number>;
    resetCallbacks: Array<() => void>;
    clearChangesCallbacks: Array<() => void>;
    postIdentityCheckCallbacks: Array<() => void>;

    constructor() {
        this.collection = ref<EditLibraries>({});
        /*
        We use a shallow ref to store the referable codes. This is because the object can be really large (fields with 10 000+ array elements), and makes the page unusable when vue dev-tools renders these objects.
         */
        this.referableCodes = shallowRef<Record<string, Record<string, CodeEditContextRefCode[]>>>({}); // codeSet -> field -> codeEditCtxRefCode
        this.fieldDefs = ref<Record<string, FieldDefinition[]>>({}); // libName --> fieldDefinitions
        this.regexValidators = ref<Record<string, RegexValidators>>({}); // codeset -> validators
        this.lastIdentityCheck = ref<string | null>(null);
        this.lastUniquenessCheck = ref<string | null>(null);
        this.newCodeSequenceNumber = ref<number>(0);
        this.resetCallbacks = [];
        this.clearChangesCallbacks = [];
        this.postIdentityCheckCallbacks = [];
    }

    reset() {
        this.collection.value = {};
        this.referableCodes.value = {};
        this.fieldDefs.value = {};
        this.regexValidators.value = {};
        for (const cb of this.resetCallbacks) {
            cb();
        }
    }

    addResetCallback(fn: () => void) {
        this.resetCallbacks.push(fn);
    }

    /*
    Registers a callback that will run after all changed fields have been reset back their unmodified state.
    Used internally by codeEditSessionState to re-apply template values after a clear-changes operation.
     */
    addClearChangesCallback(fn: () => void) {
        this.clearChangesCallbacks.push(fn);
    }

    addPostIdentityCheckCallback(fn: () => void) {
        this.postIdentityCheckCallbacks.push(fn);
    }

    nextNewTmpId(): number {
        this.newCodeSequenceNumber.value += 1;
        return newCodeDefaultId + this.newCodeSequenceNumber.value;
    }

    clearChanges() {
        this.applyToAllCodes<void>((code, fieldDefinitions) => {
            for (const a of fieldDefinitions) {
                code.modified[a.field] = code.original[a.field];
            }
        });

        // Remove relation codes that were added in current session
        for (const lib in this.collection.value) {
            for (const csName in this.collection.value[lib]) {
                const codeSet = this.collection.value[lib][csName];
                for (const codeId in codeSet) {
                    if (codeSet[codeId].metadata.deleteOnClearChanges) {
                        delete codeSet[Number.parseInt(codeId)];
                    }
                }
            }
        }

        for (const cb of this.clearChangesCallbacks) {
            cb();
        }
    }

    diff = computed<LibrariesResult<Record<string, AttributeValue>>>(() => {
        const result = this.applyToAllCodes((code, fieldDefinitions) => {
            const editDiff = codeEditFieldsDiff(code.modified, code.original, fieldDefinitions);
            if (editDiff) {
                return editDiff;
            }
        });

        return toStructuredResult(result);
    });

    isDirty = computed<boolean>(() => {
        return this.applyToAllCodes((code, fieldDefinitions) => {
            const editDiff = codeEditFieldsDiff(code.modified, code.original, fieldDefinitions);
            if (editDiff) {
                return editDiff;
            }
        }).length > 0;
    });

    requiredMissing = computed<boolean>(() => {
        const results = this.applyToAllCodes((code, fieldDefinitions) => {
            return codeEditFieldsRequiredMissing(code.modified, code.original, fieldDefinitions, code.metadata);
        });
        return results.some((x) => x.result);
    });

    async loadFromContext(id: number, r: CodeEditContextRequest, newCode: boolean) {
        const ctx = await fetchCtx(r);
        this.initializeFromEditContext(id, ctx, newCode);
    }

    initializeFieldDefinitions(library: LibraryResponse, caseSensitiveCodeRefs?: Record<string, CaseSensitiveCodeRefs>): FieldDefinition[] {
        const fieldDefinitions = createFieldDefinitionsWithCustomLabels(library);
        for (const fieldDefinition of fieldDefinitions) {
            fieldDefinition.referenceFilterDynamicCaseSensitiveCodeRefs = caseSensitiveCodeRefs?.[fieldDefinition.field];
        }
        this.setAttributes(library.name, fieldDefinitions);
        return fieldDefinitions;
    }

    initializeRegexValidators(codeSet: string, codeNameRegex: string, codeNameRegexErrorMessage: string,
        codeDescriptionRegex: string, codeDescriptionRegexErrorMessage: string) {
        if (!this.regexValidators.value[codeSet]) {
            this.regexValidators.value[codeSet] = {};
        }

        if (codeNameRegex) {
            this.regexValidators.value[codeSet].name = makeValidator(codeNameRegex, codeNameRegexErrorMessage);
        }
        if (codeDescriptionRegex) {
            this.regexValidators.value[codeSet].description = makeValidator(codeDescriptionRegex, codeDescriptionRegexErrorMessage);
        }
    }

    initializeNewCode(id: number, fieldDefinitions: FieldDefinition[], libraryName: string, codeSet: string) {
        const acode = newCodeEditFields(fieldDefinitions);
        this.setCode(libraryName, codeSet, id, acode);
    }

    initializeCode(id: number, fieldDefinitions: FieldDefinition[], libraryName: string, codeSet: string, code: CodeResponse) {
        const codeEditFields = codeEditFieldsFromCodeResponse(
            code,
            fieldDefinitions.map(a => a.field)
        );
        this.setCode(libraryName, codeSet, id, codeEditFields);
    }

    initializeFromEditContext(id: number, ctx: CodeEditContextResponse, newCode: boolean) {
        const fieldDefinitions = this.initializeFieldDefinitions(ctx.library, ctx.dynamicFilterCaseSensitiveCodeRefs);

        if (newCode) {
            this.initializeNewCode(id, fieldDefinitions, ctx.library.name, ctx.codeSet);
        } else {
            this.initializeCode(id, fieldDefinitions, ctx.library.name, ctx.codeSet, ctx.code);
        }

        this.initializeRegexValidators(ctx.codeSet, ctx.codeNameRegex, ctx.codeNameRegexErrorMessage, ctx.codeDescriptionRegex, ctx.codeDescriptionRegexErrorMessage);
        this.setValidators(ctx.codeSet, fieldDefinitions);

        this.validateInputs();
    }

    dataForCheckIdentityConflict(): ApplyToCodesResult<ChangeDocumentRequestCode>[] | null {
        if (!this.isDirty.value) {
            return null;
        }
        const result = this.applyToAllCodes((code, fieldDefinitions): ChangeDocumentRequestCode | undefined => {
            if (!code.metadata.isNew) {
                return undefined;
            }
            if (codeEditFieldsRequiredMissing(code.modified, code.original, fieldDefinitions, code.metadata)) {
                return undefined;
            }
            return createIdentityChangeDocument(code.modified, fieldDefinitions);
        });
        if (result.length > 0) {
            return result;
        }
        return null;
    }

    isIdentityConflict = computed<boolean>(() => {
        return this.applyToAllCodes((code) => {
            if (code.identityConflict) {
                return true;
            }
        }).length > 0;
    });

    async checkIdentities() {
        const identityFieldsResults = this.dataForCheckIdentityConflict();
        if (identityFieldsResults === null) {
            return;
        }
        if (JSON.stringify(identityFieldsResults) === this.lastIdentityCheck.value) {
            return;
        }
        const promises: ApplyToCodesResult<ResponsePromise<CheckIdentityResult>>[] = [];

        for (const c of identityFieldsResults) {
            promises.push({
                codeId: c.codeId,
                cs: c.cs,
                lib: c.lib,
                result: checkIdentityConflict(
                    http,
                    c.cs,
                    c.result
                )
            });
        }

        for (const r of promises) {
            const data = (await r.result).data;
            if (data.conflict) {
                this.collection.value[r.lib][r.cs][r.codeId].identityConflict = {
                    message: data.message,
                    name: data.identity,
                    id: data.id,
                    isValid: data.isValid
                };
            } else {
                delete this.collection.value[r.lib][r.cs][r.codeId].identityConflict;
                this.collection.value[r.lib][r.cs][r.codeId].modified.identity = data.identity;
            }
        }

        this.lastIdentityCheck.value = JSON.stringify(identityFieldsResults);

        for (const cb of this.postIdentityCheckCallbacks) {
            cb();
        }
    }

    dataForUniquenessCheck() {
        const result = this.applyToAllCodes((code, fieldDefinitions): UniquenessCheckRequest[] | undefined => {
            const fieldsToCheck: UniquenessCheckRequest[] = [];
            fieldDefinitions.filter(def => def.mustBeUnique).forEach((def) => {
                if (code.modified[def.field]
                    && code.modified.isValid
                    && code.modified[def.field] !== code.original[def.field]) {
                    fieldsToCheck.push({
                        field: def.field,
                        value: code.modified[def.field]!.toString()
                    });
                } else {
                    delete code.uniquenessErrors[def.field];
                }
            });
            if (fieldsToCheck.length > 0) {
                return fieldsToCheck;
            }
            return undefined;
        });

        if (result.length > 0) {
            return result.flat();
        }
        return null;
    }

    async checkUniqueness() {
        const fieldsToCheck = this.dataForUniquenessCheck();
        if (!fieldsToCheck) {
            this.lastUniquenessCheck.value = null;
            return;
        }
        if (JSON.stringify(fieldsToCheck) === this.lastUniquenessCheck.value) {
            return;
        }

        const uniquenessChecks: ApplyToCodesResult<Promise<CheckUniquenessResult>>[] = [];

        for (const c of fieldsToCheck) {
            for (const field of c.result) {
                // If we have duplicates locally, we can skip the backend check
                const localDuplicates: { id: number; value: string; codeSetName: string }[] = [];
                for (const codeId in this.collection.value[c.lib][c.cs]) {
                    const code = this.collection.value[c.lib][c.cs][codeId];
                    if (code.metadata.codeId !== c.codeId) {
                        const isValid = code.modified.isValid;
                        const value = code.modified[field.field]?.toString();
                        if (isValid && value === field.value) {
                            localDuplicates.push({
                                id: code.metadata.codeId,
                                value: value,
                                codeSetName: code.metadata.codeSetName
                            });
                        }
                    }
                }

                if (localDuplicates.length > 0) {
                    uniquenessChecks.push({
                        codeId: c.codeId,
                        cs: c.cs,
                        lib: c.lib,
                        result: Promise.resolve({
                            field: field.field,
                            hasDuplicates: true,
                            duplicates: localDuplicates
                        })
                    });
                } else {
                    uniquenessChecks.push({
                        codeId: c.codeId,
                        cs: c.cs,
                        lib: c.lib,
                        result: checkUniquenessConflict(
                            http,
                            {
                                codeId: c.codeId < newCodeDefaultId ? c.codeId : undefined,
                                codeSetName: c.cs,
                                field: field.field,
                                value: field.value
                            }
                        )
                    });
                }
            }
        }

        for (const c of uniquenessChecks) {
            const data = (await c.result);

            if (data.hasDuplicates) {
                // Conflicts may be false positives if the duplicate is also being edited
                const realDuplicate = data.duplicates.find(dupe => {
                    return !this.collection.value[c.lib][c.cs][dupe.id]
                        || this.collection.value[c.lib][c.cs][dupe.id].modified[data.field] === dupe.value;
                });

                if (realDuplicate) {
                    this.collection.value[c.lib][c.cs][c.codeId].uniquenessErrors[data.field] = {
                        type: ErrorType.Error,
                        message: 'Value is in conflict with another code. This field must be unique among valid codes'
                    };
                } else {
                    delete this.collection.value[c.lib][c.cs][c.codeId].uniquenessErrors[data.field];
                }
            } else {
                delete this.collection.value[c.lib][c.cs][c.codeId].uniquenessErrors[data.field];
            }
        }
        this.lastUniquenessCheck.value = JSON.stringify(fieldsToCheck);
    }

    initCodeSet(libName: string, codeSetName: string) {
        if (!this.collection.value[libName]) {
            this.collection.value[libName] = {};
        }
        if (!this.collection.value[libName][codeSetName]) {
            this.collection.value[libName][codeSetName] = {};
        }
    }

    setCode(libName: string, codeSetName: string, codeId: number, code: CodeEditFields) {
        this.collection.value[libName][codeSetName][codeId] = {
            original: structuredClone(code),
            modified: structuredClone(code),
            validationErrors: {},
            uniquenessErrors: {},
            metadata: {
                libraryName: libName,
                codeSetName: codeSetName,
                codeId: codeId,
                isNew: codeId >= newCodeDefaultId
            }
        };
    }

    setAttributes(libName: string, fieldDefinitions: FieldDefinition[]) {
        this.fieldDefs.value[libName] = structuredClone(fieldDefinitions);
    }

    setValidators(csName: string, fieldDefinitions: FieldDefinition[]) {
        if (!this.regexValidators.value[csName]) {
            this.regexValidators.value[csName] = {};
        }
        fieldDefinitions.forEach(fd => {
            if (fd.regex) {
                this.regexValidators.value[csName][fd.field] = makeValidator(fd.regex, fd.regexErrorMessage);
            }
        });
    }

    setReferableCodes(codeSet: string, refCodes: Record<string, CodeEditContextRefCode[]>) {
        this.referableCodes.value[codeSet] = refCodes;
    }

    setFieldValue(libraryName: string, codeSetName: string, codeId: number, field: string, value: AttributeValue) {
        const code = this.collection.value[libraryName][codeSetName][codeId];
        if (!Object.hasOwn(code.modified, field)) {
            throw new Error(`Code has no field named [${field}]`);
        }
        code.modified[field] = value;
    }

    getReferableCodeByIdentity(codeSetName: string, field: string, identity: string): CodeEditContextRefCode | undefined {
        return this.referableCodes.value[codeSetName]?.[field]?.find(code => code.identity === identity);
    }

    /**
     * Validates with regex, creating a critical error if the code is new, or a warning if the code is existing and the value has not changed.
     * @throws {Error} If any code's modified CodeEditFields object is missing a field
     * defined in the corresponding fieldDefinitions.
     */
    validateInputs() {
        this.applyToAllCodes((code, fieldDefinitions) => {
            const validators = this.regexValidators.value[code.metadata.codeSetName];
            for (const fieldDefinition of fieldDefinitions) {
                const modifiedValue = code.modified[fieldDefinition.field];
                if (modifiedValue === undefined) {
                    throw new Error(`modified value for attribute ${fieldDefinition.field} is undefined`);
                }
                // warning if existing and not changed, else: danger
                let newValidationError = null;
                const oldValidationError = code.validationErrors[fieldDefinition.field];

                const errorType = code.metadata.isNew
                    ? ErrorType.Error
                    : modifiedValue !== code.original[fieldDefinition.field]
                        ? ErrorType.Error
                        : ErrorType.Warning;

                if (!isHidden(fieldDefinition) && fieldDefinition.required && !codeEditRequiredFieldValidValue(modifiedValue)) {
                    newValidationError = {
                        message: `Attribute ${fieldDefinition.field} is required`,
                        type: errorType
                    };
                }

                const fieldValidator = validators[fieldDefinition.field];
                let validationResult;

                if (fieldValidator) {
                    validationResult = fieldValidator.validate(modifiedValue);
                }

                if (validationResult) {
                    newValidationError = {
                        message: validationResult.error,
                        type: errorType
                    };
                } else if (isStringField(fieldDefinition, modifiedValue)
                    && modifiedValue !== null
                    && modifiedValue.trim().length === 0
                    && !fieldDefinition.required
                    && fieldDefinition.field !== 'name') {
                    // validate that text input has not been cleared any other way then by clicking the clear field button
                    //
                    // If we need more of these types of validators in the future, we should consider allowing a per-datatype
                    // list of validators in the editsession that can be modified by the component that consumes the editsessionstore.
                    // I think it is fine for now, but the error messages here will not make sense for all UIs that may use the
                    // editsessionstore in the future.
                    newValidationError = {
                        message: (
                            modifiedValue.length === 0
                                ? 'Confirm clearing this attribute by clicking the Clear field button to the right'
                                : 'Input field contains whitespace. Use right side buttons to clear or reset input'
                        ),
                        type: errorType
                    };
                }

                if (choiceViolation(modifiedValue, fieldDefinition.choices)) {
                    newValidationError = {
                        message: `[${modifiedValue}] is not a valid value`,
                        type: errorType
                    };
                }

                // Only let the dynamic filter message replace an existing one when it does not weaken the severity,
                // so a blocking error (for instance a missing required value) is never downgraded to a warning
                const filterViolation = this.dynamicFilterViolation(code, fieldDefinition, fieldDefinitions);
                if (filterViolation !== null
                    && (newValidationError === null
                        || filterViolation.type === ErrorType.Error
                        || newValidationError.type !== ErrorType.Error)) {
                    newValidationError = filterViolation;
                }

                // Do not modify if there are no changes to not re-trigger the watch function
                if (newValidationError !== null && _.isEqual(oldValidationError, newValidationError)) {
                    continue;
                }
                if (newValidationError !== null) {
                    code.validationErrors[fieldDefinition.field] = newValidationError;
                    continue;
                }
                delete code.validationErrors[fieldDefinition.field];
            }
        });
    }

    private dynamicFilterViolation(code: CodeEditSessionItem, fieldDefinition: FieldDefinition, fieldDefinitions: FieldDefinition[]): ValidationError | null {
        const predicate = fieldDefinition.referenceFilterDynamic;
        if (!predicate || fieldDefinition.type !== 'CodeRef') {
            return null;
        }

        const identity = code.modified[fieldDefinition.field];

        // The attribute/param the filter depends on was deleted: can't evaluate the filter reliably,
        // so warn always, but only block save if the user is the one setting/changing this field's value.
        // Changes to other fields on the same code are unaffected and can still be saved.
        const ownFieldNames = new Set(fieldDefinitions.map(fd => fd.field));
        const brokenDependency = brokenDynamicFilterDependency(
            predicate,
            ownFieldNames,
            this.referableCodes.value[code.metadata.codeSetName]?.[fieldDefinition.field]
        );
        if (brokenDependency) {
            const hasValue = identity !== null && identity !== undefined && identity !== '';
            const userCaused = hasValue && (code.metadata.isNew || identity !== code.original[fieldDefinition.field]);
            return userCaused
                ? {
                    message: `Cannot save: the dynamic filter for this attribute depends on '${brokenDependency}', which no longer exists. The dynamic filter must be removed or updated before this value can be changed.`,
                    type: ErrorType.Error
                }
                : {
                    message: `Warning: the dynamic filter for this attribute depends on '${brokenDependency}', which no longer exists. Update or remove the dynamic filter for this attribute.`,
                    type: ErrorType.Warning
                };
        }

        const option = typeof identity === 'string'
            ? this.getReferableCodeByIdentity(code.metadata.codeSetName, fieldDefinition.field, identity)
            : undefined;
        if (!option) {
            return null;
        }
        const caseSensitiveCodeRefs = fieldDefinition.referenceFilterDynamicCaseSensitiveCodeRefs;
        const { matched, failing, failingNodes } = evaluateReferenceFilter(predicate, option.dynamicFilterReferenceValues ?? null, code.modified, caseSensitiveCodeRefs);
        if (matched) {
            return null;
        }

        // A param counts as changed only when the failing rule can see it, so a time below a second
        // is not, nor is new casing unless the rule is case-sensitive. A param of unknown type falls
        // back to an exact comparison, as on the server.
        const userCaused = code.metadata.isNew
            || identity !== code.original[fieldDefinition.field]
            || failingNodes.some(node => {
                const param = node.param;
                if (!param) {
                    return false;
                }
                const type = fieldDefinitions.find(fd => fd.field === param)?.type;
                return type
                    ? valueChangedForFilter(typeGroup(type), code.original[param], code.modified[param], isCaseSensitive(node, caseSensitiveCodeRefs))
                    : code.modified[param] !== code.original[param];
            });

        const hint = fieldDefinition.referenceFilterDynamicErrorMessage;
        return userCaused
            ? { message: referenceFilterMismatchMessage(failing), type: ErrorType.Error }
            : {
                message: hint
                    ? `Warning: reference no longer recommended (${hint})`
                    : 'Warning: reference no longer recommended',
                type: ErrorType.Warning
            };
    }

    hasInputErrors = computed<boolean>(() => {
        return this.applyToAllCodes((code) => {
            for (const k in code.validationErrors) {
                if (code.validationErrors[k].type === ErrorType.Error) {
                    return true;
                }
            }

            for (const k in code.uniquenessErrors) {
                if (code.uniquenessErrors[k].type === ErrorType.Error) {
                    return true;
                }
            }
        }).length > 0;
    });

    applyToAllLibraries<T>(f: ApplyToLibraryFunc<T>): ApplyToLibrariesResult<T>[] {
        const results: ApplyToLibrariesResult<T>[] = [];
        for (const lib in this.collection.value) {
            const fieldDef = this.fieldDefs.value[lib];
            const result = f(
                this.collection.value[lib],
                fieldDef,
                {
                    libraryName: lib
                }
            );
            if (result === undefined) {
                continue;
            }

            results.push({
                lib,
                result
            });
        }
        return results;
    }

    applyToAllCodeSets<T>(f: ApplyToCodeSetFunc<T>): ApplyToCodeSetsResult<T>[] {
        const results: ApplyToCodeSetsResult<T>[] = [];
        for (const lib in this.collection.value) {
            const fieldDef = this.fieldDefs.value[lib];
            for (const csName in this.collection.value[lib]) {
                const result = f(
                    this.collection.value[lib][csName],
                    fieldDef,
                    {
                        libraryName: lib,
                        codeSetName: csName
                    }
                );
                if (result === undefined) {
                    continue;
                }

                results.push({
                    lib,
                    cs: csName,
                    result
                });
            }
        }
        return results;
    }

    /*
    Provide a function to be applied to all codes in context
    An undefined or missing return in a function will not be included in the result structure
     */
    applyToAllCodes<T>(f: ApplyToCodeFunc<T>): ApplyToCodesResult<T>[] {
        const results: ApplyToCodesResult<T>[] = [];
        for (const lib in this.collection.value) {
            const fieldDef = this.fieldDefs.value[lib];
            for (const csName in this.collection.value[lib]) {
                for (const codeId in this.collection.value[lib][csName]) {
                    const result = f(
                        this.collection.value[lib][csName][codeId],
                        fieldDef
                    );
                    if (result === undefined) {
                        continue;
                    }

                    results.push({
                        lib,
                        cs: csName,
                        codeId: parseInt(codeId),
                        result
                    });
                }
            }
        }
        return results;
    }

    numCodeSetsWithChanges = computed<number>(() => {
        const result = this.applyToAllCodes((code, fieldDefinitions) => {
            if (!code.metadata.isNew && !codeEditFieldsDiff(code.modified, code.original, fieldDefinitions)) {
                return undefined;
            }
            return true;
        })
            .map(c => c.cs);

        return new Set(result).size;
    });

    canSubmit = computed((): boolean => {
        return !this.requiredMissing.value
            && this.isDirty.value
            && !this.hasInputErrors.value
            && !this.isIdentityConflict.value;
    });

    canSubmitCauses = computed(() => {
        return {
            requiredMissing: this.requiredMissing.value,
            isDirty: this.isDirty.value,
            hasInputErrors: this.hasInputErrors.value,
            isIdentityConflict: this.isIdentityConflict.value
        };
    });
}

export function toStructuredResult<T>(results: ApplyToCodesResult<T>[]): LibrariesResult<T> {
    const structuredRes: LibrariesResult<T> = {};
    for (const entry of results) {
        if (!(entry.lib in structuredRes)) {
            structuredRes[entry.lib] = {};
        }

        if (!(entry.cs in structuredRes[entry.lib])) {
            structuredRes[entry.lib][entry.cs] = {};
        }

        structuredRes[entry.lib][entry.cs][entry.codeId] = entry.result;
    }
    return structuredRes;
}

export async function fetchCtx(r: CodeEditContextRequest): Promise<CodeEditContextResponse> {
    return (await fetchCodeEditContext(http, r)).data;
}
